/**
 * @fileoverview Signature image token shared by the document fill/render
 * pipelines and the frontend (MOR-845).
 *
 * A signature image travels through field-values maps as the token
 * `__sig_image__:<url>`, optionally carrying the signatory's size
 * multiplier as a leading `s=<scale>;` segment:
 *
 *   __sig_image__:https://…                (default size)
 *   __sig_image__:s=1.25;https://…         (drawn 25% larger)
 *
 * The scale is the user's `signatureScale` preference (0.5–1.5, default 1)
 * and is applied on top of each renderer's base size — the 40pt cap on PDF
 * overlays, the widget rectangle on AcroForm fills, and the 60px/240px CSS
 * bounds on HTML letters.
 */

export const SIG_IMAGE_PREFIX = "__sig_image__:";

const SCALE_SEGMENT = /^s=([0-9]+(?:\.[0-9]+)?);/;

/** Clamp a stored scale to the range the settings UI offers. */
function clampScale(scale: number): number {
  if (!Number.isFinite(scale) || scale <= 0) return 1;
  return Math.min(Math.max(scale, 0.5), 1.5);
}

/** Build the token for a signature image URL, embedding a non-default scale. */
export function buildSignatureImageToken(
  url: string,
  scale?: number | null,
): string {
  const clamped = scale === undefined || scale === null ? 1 : clampScale(scale);
  return Math.abs(clamped - 1) < 0.005
    ? `${SIG_IMAGE_PREFIX}${url}`
    : `${SIG_IMAGE_PREFIX}s=${clamped};${url}`;
}

/**
 * Parse a token back into its URL + scale. Values without the scale
 * segment (all pre-MOR-845 tokens) parse with scale 1.
 */
export function parseSignatureImageToken(value: string): {
  url: string;
  scale: number;
} {
  const rest = value.startsWith(SIG_IMAGE_PREFIX)
    ? value.slice(SIG_IMAGE_PREFIX.length)
    : value;
  const match = SCALE_SEGMENT.exec(rest);
  if (!match) return { url: rest, scale: 1 };
  return {
    url: rest.slice(match[0].length),
    scale: clampScale(Number(match[1])),
  };
}
